import { ReactElement, useCallback, useMemo, useState } from "react";

import { Box, Step, StepLabel, Stepper } from "@portex-pro/ui-components";
import findIndex from "lodash/findIndex";
import first from "lodash/first";
import takeRight from "lodash/takeRight";
import { useHistory } from "react-router-dom";

import { Mode } from "../../../../../../api/types/generated-types";
import NotFound404 from "../../../../../../components/errors/NotFound404";
import LayoutColumnTwo from "../../../../../../components/LayoutColumnTwo";
import PartnersStep from "../../components/PartnersStep";
import RequestQuoteFlowSuccess from "../../components/RequestQuoteFlowSuccess";
import ReviewStep from "../../components/ReviewStep";
import { HandleGoToStep, HandleStepperLoading } from "../../components/StepperStep";
import TeamsStep from "../../components/TeamsStep";
import ContainersStep from "./components/ContainersStep";
import LaneStep from "./components/LaneStep";
import QuotePreferencesStep from "./components/QuotePreferencesStep";
import RoutingStep from "./components/RoutingStep";
import ShipmentDetailsStep from "./components/ShipmentDetailsStep";
import { STEPS } from "./constants/fclSteps";
import { ResetProgress, StepsFCL, StepsProgress } from "./types/StepsFCL";
import { generatePathNextStepFCL } from "./utils/generateNextStepFCL";

const RequestQuoteFlowFCLPage = (): ReactElement => {
  const history = useHistory();
  const [progress, setProgress] = useState<StepsProgress>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [goToStep, setGoToStep] = useState<StepsFCL[number]>("");
  const [resetting, setResetting] = useState<boolean>(false);

  const handleGoToStep: HandleGoToStep = useCallback(
    (success, step) => {
      if (success) history.push(generatePathNextStepFCL(step || goToStep, history));
      setGoToStep("");
    },
    [goToStep, history]
  );
  const handleLoading: HandleStepperLoading = useCallback((loading) => setLoading(loading), []);
  const handleResetProgress: ResetProgress = useCallback(() => {
    setResetting(true);
    setProgress({ [StepsFCL.Lane]: true });
    setResetting(false);
  }, []);

  const activeStepIndex = useMemo<number>(() => {
    const path = first(takeRight(history.location.pathname.split("/")));

    const currentStep = (() => {
      switch (path) {
        case Mode.Fcl:
        case StepsFCL.Lane: {
          return StepsFCL.Lane;
        }
        case StepsFCL.Routing:
        case StepsFCL.Containers:
        case StepsFCL.QuotePreferences:
        case StepsFCL.Shipment:
        case StepsFCL.Partners:
        case StepsFCL.Teams:
        case StepsFCL.Review:
        case StepsFCL.Done: {
          return path;
        }
        default: {
          return undefined;
        }
      }
    })();

    if (currentStep) setProgress((p) => ({ ...p, [currentStep]: true }));

    return findIndex(STEPS, ["routeName", currentStep]);
  }, [history.location.pathname]);

  const isActive = useCallback(
    (step: StepsFCL) => {
      return step === STEPS[activeStepIndex]?.routeName;
    },
    [activeStepIndex]
  );

  const notFound = activeStepIndex === -1;

  return (
    <LayoutColumnTwo>
      {activeStepIndex > -1 ? (
        <LayoutColumnTwo.Sidebar>
          <Box mt={1}>
            <Stepper activeStep={activeStepIndex} orientation="vertical">
              {STEPS.filter((step) => !step.hide).map((step, i) => {
                const isActiveStep = activeStepIndex === i;
                const disabled = loading || isActiveStep || (!progress[step.routeName] && i > activeStepIndex);
                let cursor = "unset";

                if (isActiveStep) cursor = "auto";
                else if (loading) cursor = "wait";
                else if (disabled) cursor = "not-allowed";
                else cursor = "pointer";

                return (
                  <Step key={i}>
                    <StepLabel
                      style={{ userSelect: "none", cursor }}
                      onClick={() => {
                        if (disabled) return;
                        setGoToStep(step.routeName);
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </LayoutColumnTwo.Sidebar>
      ) : null}

      <LaneStep
        active={isActive(StepsFCL.Lane)}
        nextStep={StepsFCL.Routing}
        goToStep={goToStep}
        onGoToStep={handleGoToStep}
        onLoading={handleLoading}
        onResetProgress={handleResetProgress}
      />

      {resetting ? null : (
        <>
          <RoutingStep
            active={isActive(StepsFCL.Routing)}
            prevStep={StepsFCL.Lane}
            nextStep={StepsFCL.Containers}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <ContainersStep
            active={isActive(StepsFCL.Containers)}
            prevStep={StepsFCL.Routing}
            nextStep={StepsFCL.QuotePreferences}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <QuotePreferencesStep
            active={isActive(StepsFCL.QuotePreferences)}
            prevStep={StepsFCL.Containers}
            nextStep={StepsFCL.Shipment}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <ShipmentDetailsStep
            active={isActive(StepsFCL.Shipment)}
            prevStep={StepsFCL.QuotePreferences}
            nextStep={StepsFCL.Partners}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <PartnersStep
            active={isActive(StepsFCL.Partners)}
            prevStep={StepsFCL.Shipment}
            nextStep={StepsFCL.Teams}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <TeamsStep
            active={isActive(StepsFCL.Teams)}
            prevStep={StepsFCL.Partners}
            nextStep={StepsFCL.Review}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <ReviewStep
            active={isActive(StepsFCL.Review)}
            prevStep={StepsFCL.Teams}
            nextStep={StepsFCL.Done}
            goToStep={goToStep}
            onGoToStep={handleGoToStep}
            onLoading={handleLoading}
          />
          <RequestQuoteFlowSuccess active={isActive(StepsFCL.Done)} />
        </>
      )}

      {notFound ? <NotFound404 /> : null}
    </LayoutColumnTwo>
  );
};

export default RequestQuoteFlowFCLPage;
